import { z } from 'zod';
import { command, get, getApiClient, post } from '../http';
import { resource } from '../schemas/common';
import { sessionSchema, userSchema } from '../schemas/domain';

export const loginInput = z.object({
  /** Accepts either the member code (AO123456) or the email address. */
  identifier: z.string().min(3, 'Enter your User ID or email'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  remember: z.boolean().optional(),
});
export type LoginInput = z.infer<typeof loginInput>;

export const registerInput = z
  .object({
    sponsor_code: z.string().min(1, 'Sponsor ID is required'),
    name: z.string().min(2),
    email: z.string().email(),
    phone: z.string().min(6),
    password: z.string().min(8),
    password_confirmation: z.string(),
    /** Binary placement leg — separate from the sponsor (unilevel) tree. */
    leg: z.enum(['left', 'right']).optional(),
    accept_terms: z.literal(true),
  })
  .refine((v) => v.password === v.password_confirmation, {
    message: 'Passwords do not match',
    path: ['password_confirmation'],
  });
export type RegisterInput = z.infer<typeof registerInput>;

export const twoFactorInput = z.object({
  code: z.string().length(6, 'Enter the 6-digit code'),
});
export type TwoFactorInput = z.infer<typeof twoFactorInput>;

export const forgotPasswordInput = z.object({ email: z.string().email() });
export type ForgotPasswordInput = z.infer<typeof forgotPasswordInput>;

export const resetPasswordInput = z
  .object({
    token: z.string(),
    email: z.string().email(),
    password: z.string().min(8),
    password_confirmation: z.string(),
  })
  .refine((v) => v.password === v.password_confirmation, {
    message: 'Passwords do not match',
    path: ['password_confirmation'],
  });
export type ResetPasswordInput = z.infer<typeof resetPasswordInput>;

const loginResponse = z.object({
  /** Server may demand a TOTP step before issuing the session cookie. */
  requires_two_factor: z.boolean().default(false),
  session: sessionSchema.nullable().default(null),
});

export const authApi = {
  /** Must run before the first mutating request in Sanctum SPA cookie mode. */
  csrf: () => getApiClient().ensureCsrfCookie(true),

  login: async (input: LoginInput) => {
    await getApiClient().ensureCsrfCookie();
    return post('/auth/login', loginResponse, input);
  },

  /** Admin panel login — hits the admin guard (mandatory 2FA, admin audit trail). */
  adminLogin: async (input: LoginInput) => {
    await getApiClient().ensureCsrfCookie();
    return post('/admin/login', loginResponse, input);
  },

  verifyTwoFactor: (input: TwoFactorInput) => post('/auth/two-factor', resource(sessionSchema), input),

  register: (input: RegisterInput) => post('/auth/register', resource(userSchema), input),

  logout: () => command('/auth/logout'),

  refresh: () => getApiClient().refreshSession(),

  me: () => get('/auth/me', resource(sessionSchema)),

  forgotPassword: (input: ForgotPasswordInput) => command('/auth/forgot-password', input),

  resetPassword: (input: ResetPasswordInput) => command('/auth/reset-password', input),

  /** Validates a sponsor code on the register form before submit. */
  lookupSponsor: (code: string) =>
    get('/auth/sponsor-lookup', resource(z.object({ user_code: z.string(), name: z.string() })), {
      code,
    }),
};
